import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    ArrowLeft,
    Shield,
    Eye,
    Lock,
    Server,
    UserCheck,
    Bell,
    Trash2,
    Globe,
    Key,
    Target,
} from 'lucide-react';

const sections = [
    {
        id: 'collection',
        icon: Eye,
        titleKey: 'privacy.sections.collection.title',
        contentKey: 'privacy.sections.collection.content',
    },
    {
        id: 'usage',
        icon: Server,
        titleKey: 'privacy.sections.usage.title',
        contentKey: 'privacy.sections.usage.content',
    },
    {
        id: 'storage',
        icon: Lock,
        titleKey: 'privacy.sections.storage.title',
        contentKey: 'privacy.sections.storage.content',
    },
    {
        id: 'sharing',
        icon: Globe,
        titleKey: 'privacy.sections.sharing.title',
        contentKey: 'privacy.sections.sharing.content',
    },
    {
        id: 'rights',
        icon: UserCheck,
        titleKey: 'privacy.sections.rights.title',
        contentKey: 'privacy.sections.rights.content',
    },
    {
        id: 'cookies',
        icon: Bell,
        titleKey: 'privacy.sections.cookies.title',
        contentKey: 'privacy.sections.cookies.content',
    },
    {
        id: 'deletion',
        icon: Trash2,
        titleKey: 'privacy.sections.deletion.title',
        contentKey: 'privacy.sections.deletion.content',
    },
    {
        id: 'security',
        icon: Key,
        titleKey: 'privacy.sections.security.title',
        contentKey: 'privacy.sections.security.content',
    },
];

export default function PrivacyPage() {
    const { t } = useTranslation();

    return (
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex flex-col">
            {/* Header */}
            <header className="bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/" className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg">
                            <ArrowLeft className="w-5 h-5" />
                        </Link>
                        <div className="flex items-center gap-2">
                            <Shield className="w-5 h-5 text-primary-600" />
                            <h1 className="text-xl font-bold">{t('privacy.title', 'Chính sách Bảo mật')}</h1>
                        </div>
                    </div>
                    <Link to="/login" className="btn-primary text-sm">
                        {t('auth.login')}
                    </Link>
                </div>
            </header>

            {/* Content */}
            <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
                {/* Hero */}
                <section className="text-center py-8">
                    <div className="w-16 h-16 bg-primary-100 dark:bg-primary-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Shield className="w-8 h-8 text-primary-600" />
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                        {t('privacy.hero.title', 'Quyền riêng tư của bạn là ưu tiên hàng đầu')}
                    </h2>
                    <p className="text-neutral-600 dark:text-neutral-400 max-w-xl mx-auto mb-4">
                        {t('privacy.hero.subtitle', 'Chính sách này giải thích cách VisionBoard thu thập, sử dụng và bảo vệ thông tin cá nhân của bạn.')}
                    </p>
                    <p className="text-sm text-neutral-500">
                        {t('privacy.hero.lastUpdated', 'Cập nhật lần cuối: 11 tháng 12, 2024')}
                    </p>
                </section>

                {/* Quick Summary */}
                <section className="bg-gradient-to-br from-primary-600 to-secondary-600 rounded-2xl p-6 text-white mb-8">
                    <h3 className="font-semibold text-lg mb-3">
                        {t('privacy.summary.title', 'Tóm tắt nhanh')}
                    </h3>
                    <ul className="space-y-2 text-sm text-white/90">
                        <li className="flex items-start gap-2">
                            <span className="text-white/60">•</span>
                            {t('privacy.summary.item1', 'Chúng tôi chỉ thu thập thông tin cần thiết để cung cấp dịch vụ')}
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-white/60">•</span>
                            {t('privacy.summary.item2', 'Dữ liệu của bạn được mã hóa và lưu trữ an toàn')}
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-white/60">•</span>
                            {t('privacy.summary.item3', 'Chúng tôi không bán thông tin của bạn cho bên thứ ba')}
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-white/60">•</span>
                            {t('privacy.summary.item4', 'Bạn có quyền truy cập, sửa đổi và xóa dữ liệu của mình')}
                        </li>
                    </ul>
                </section>

                {/* Sections */}
                <div className="space-y-6">
                    {sections.map((section, idx) => {
                        const Icon = section.icon;
                        return (
                            <section
                                key={section.id}
                                id={section.id}
                                className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-700"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 bg-primary-100 dark:bg-primary-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <Icon className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold mb-3">
                                            {idx + 1}. {t(section.titleKey, section.id)}
                                        </h3>
                                        <div className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed space-y-3">
                                            {section.id === 'collection' && (
                                                <>
                                                    <p>{t('privacy.sections.collection.content', 'Chúng tôi thu thập các loại thông tin sau:')}</p>
                                                    <ul className="list-disc list-inside space-y-1 ml-2">
                                                        <li>{t('privacy.sections.collection.item1', 'Thông tin tài khoản: Họ tên, email, mật khẩu (đã mã hóa)')}</li>
                                                        <li>{t('privacy.sections.collection.item2', 'Dữ liệu sử dụng: Các mục tiêu, công việc và tiến độ bạn tạo')}</li>
                                                        <li>{t('privacy.sections.collection.item3', 'Thông tin thiết bị: Loại trình duyệt, địa chỉ IP (ẩn danh)')}</li>
                                                    </ul>
                                                </>
                                            )}
                                            {section.id === 'usage' && (
                                                <>
                                                    <p>{t('privacy.sections.usage.content', 'Chúng tôi sử dụng thông tin để:')}</p>
                                                    <ul className="list-disc list-inside space-y-1 ml-2">
                                                        <li>{t('privacy.sections.usage.item1', 'Cung cấp và cải thiện dịch vụ VisionBoard')}</li>
                                                        <li>{t('privacy.sections.usage.item2', 'Gửi thông báo quan trọng về tài khoản')}</li>
                                                        <li>{t('privacy.sections.usage.item3', 'Phát hiện và ngăn chặn gian lận, lạm dụng')}</li>
                                                        <li>{t('privacy.sections.usage.item4', 'Phân tích cách người dùng sử dụng dịch vụ (ẩn danh)')}</li>
                                                    </ul>
                                                </>
                                            )}
                                            {section.id === 'storage' && (
                                                <p>{t('privacy.sections.storage.content', 'Dữ liệu của bạn được lưu trữ trên hạ tầng đám mây an toàn (Cloudflare) với mã hóa AES-256. Chúng tôi duy trì các bản sao lưu định kỳ để đảm bảo an toàn dữ liệu. Dữ liệu được lưu trữ tại các trung tâm dữ liệu đạt chuẩn ISO 27001.')}</p>
                                            )}
                                            {section.id === 'sharing' && (
                                                <>
                                                    <p>{t('privacy.sections.sharing.content', 'Chúng tôi không bán thông tin của bạn. Chúng tôi chỉ chia sẻ dữ liệu trong các trường hợp sau:')}</p>
                                                    <ul className="list-disc list-inside space-y-1 ml-2">
                                                        <li>{t('privacy.sections.sharing.item1', 'Với các nhà cung cấp dịch vụ đáng tin cậy (email, hosting)')}</li>
                                                        <li>{t('privacy.sections.sharing.item2', 'Khi pháp luật yêu cầu')}</li>
                                                        <li>{t('privacy.sections.sharing.item3', 'Với sự đồng ý rõ ràng của bạn')}</li>
                                                    </ul>
                                                </>
                                            )}
                                            {section.id === 'rights' && (
                                                <>
                                                    <p>{t('privacy.sections.rights.content', 'Bạn có các quyền sau đây:')}</p>
                                                    <ul className="list-disc list-inside space-y-1 ml-2">
                                                        <li>{t('privacy.sections.rights.item1', 'Quyền truy cập: Xem dữ liệu chúng tôi lưu về bạn')}</li>
                                                        <li>{t('privacy.sections.rights.item2', 'Quyền chỉnh sửa: Cập nhật thông tin cá nhân')}</li>
                                                        <li>{t('privacy.sections.rights.item3', 'Quyền xóa: Yêu cầu xóa tài khoản và dữ liệu')}</li>
                                                        <li>{t('privacy.sections.rights.item4', 'Quyền xuất dữ liệu: Tải xuống dữ liệu của bạn')}</li>
                                                    </ul>
                                                </>
                                            )}
                                            {section.id === 'cookies' && (
                                                <p>{t('privacy.sections.cookies.content', 'Chúng tôi sử dụng cookies để: duy trì phiên đăng nhập, lưu tùy chọn (ngôn ngữ, giao diện), và phân tích lưu lượng truy cập. Bạn có thể tắt cookies trong trình duyệt, nhưng một số tính năng có thể không hoạt động đúng.')}</p>
                                            )}
                                            {section.id === 'deletion' && (
                                                <p>{t('privacy.sections.deletion.content', 'Bạn có thể xóa tài khoản bất kỳ lúc nào trong Cài đặt > Bảo mật > Xóa tài khoản. Khi xóa, tất cả dữ liệu của bạn (mục tiêu, công việc, board) sẽ bị xóa vĩnh viễn trong vòng 30 ngày. Một số dữ liệu có thể được giữ lại theo yêu cầu pháp luật.')}</p>
                                            )}
                                            {section.id === 'security' && (
                                                <>
                                                    <p>{t('privacy.sections.security.content', 'Chúng tôi bảo vệ dữ liệu của bạn bằng:')}</p>
                                                    <ul className="list-disc list-inside space-y-1 ml-2">
                                                        <li>{t('privacy.sections.security.item1', 'Mã hóa SSL/TLS cho tất cả kết nối')}</li>
                                                        <li>{t('privacy.sections.security.item2', 'Mã hóa mật khẩu với PBKDF2')}</li>
                                                        <li>{t('privacy.sections.security.item3', 'Xác thực hai yếu tố (2FA) tùy chọn')}</li>
                                                        <li>{t('privacy.sections.security.item4', 'Kiểm tra bảo mật định kỳ')}</li>
                                                    </ul>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </section>
                        );
                    })}
                </div>

                {/* Contact */}
                <section className="mt-8 text-center py-8 bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-200 dark:border-neutral-700">
                    <h3 className="text-xl font-bold mb-2">
                        {t('privacy.contact.title', 'Câu hỏi về quyền riêng tư?')}
                    </h3>
                    <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                        {t('privacy.contact.desc', 'Liên hệ với chúng tôi qua email')}
                    </p>
                    <a href="mailto:support@1vision.work" className="text-primary-600 hover:underline font-medium">
                        support@1vision.work
                    </a>
                </section>
            </main>

            {/* Footer */}
            <footer className="py-8 px-4 bg-neutral-100 dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-700 mt-auto">
                <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                            <Target className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-neutral-900 dark:text-white">VisionBoard</span>
                    </Link>
                    <div className="flex items-center gap-4 text-sm text-neutral-500">
                        <Link to="/terms" className="hover:text-primary-600">Điều khoản</Link>
                        <Link to="/contact" className="hover:text-primary-600">Liên hệ</Link>
                        <Link to="/help" className="hover:text-primary-600">Hỗ trợ</Link>
                    </div>
                    <p className="text-sm text-neutral-500">© 2025 VisionBoard</p>
                </div>
            </footer>
        </div>
    );
}
